import { Recommendation } from "./Recommendation.js";
import {
  FuelSource,
  FuelSourceThatSupportsEfficiency,
  FuelSourceThatSupportsElectrification,
} from "./FuelSource.js";
import { EmissionsBySource } from "./EmissionsBySource";
import { Scope } from "./Solution.js";

export type FuelImpact = {
  fuelSource: FuelSource;
  carbonImpact: number;
};

export enum PlanComponentCategory {
  CutEnergyWaste = "cutEnergyWaste",
  ElectrifyMobile = "electrifyMobile",
  ElectrifyStationary = "electrifyStationary",
  BuyClean = "buyClean",
  Other = "other",
}

export type PlanComponent = {
  planComponentCategory: PlanComponentCategory;
  intermediateCostScore: number;
  fuelImpacts: FuelImpact[]; // account for multiple fuel impacts - e.g. electrification can reduce gas but reduce electricity
  carbonImpact: number;
};

export type NullablePlanComponent = PlanComponent | null;

export type CarbonImpactByScope = Record<Scope, number>;

export type ImpactByScopeAndCategory = Record<
  Scope,
  Partial<Record<PlanComponentCategory, number>>
>;

export type AnalyzedPlan = {
  costScore: number;
  carbonImpact: number;
  gap: number;
  businessGrowth: number;
  cleanerGrid: number;
  baselineCarbonWithGrowthAndCleanerGrid: EmissionsBySource;
  footprintAfterPlan: EmissionsBySource;
  emissionsRemainingAfterPlan: number;
  planAbsoluteImpact: number;
  planComponents: PlanComponent[];
  impactByScopeAndCategory: ImpactByScopeAndCategory;
  carbonImpactByScope: CarbonImpactByScope;
  recommendations: Recommendation[];
};

// returned by efficiency and electrification components and used by other components
// further down the line that rely on their values. e.g. fuel switching depends on
// the amount of each fuel left over after electrification and efficiency
export type IntermediateResults = {
  unitsOfEnergy: number;
  unitsOfEnergyAfterReduction: number;
  unitsOfCarbonAfterReduction: number;
  carbonImpact: number;
  costScore: number;
  solutionPercent: number;
  fuelImpacts: FuelImpact[];
};

export type EfficiencyResultsByFuel = Record<
  FuelSourceThatSupportsEfficiency,
  IntermediateResults
>;

export type ElectrificationResultsByFuel = Record<
  FuelSourceThatSupportsElectrification,
  IntermediateResults
>;
